import React, { useEffect } from 'react'
import * as faceApi from "face-api.js"

/**
 * @typedef {object} Props
 * @prop {React.Dispatch<boolean>} setFaceState
 * @prop {React.Dispatch<MediaStream>} setStream
 * @prop {Function} startRecording
 * @prop {React.MutableRefObject<HTMLVideoElement>} videoRefProp
 * @prop {React.MutableRefObject<HTMLCanvasElement>} canvasRef
 */

/**
 *
 *
 * @param {Props} props
 * @return {JSX.Element} 
 */
const FaceRecognition = ({
    setFaceState,
    setStream,
    startRecording,
    videoRefProp,
    canvasRef,
}) => {
    let isValid = false

    const videoWidth = window.innerWidth < 300 ? window.innerWidth : 300;
    const videoHeight = window.innerHeight < 300 ? window.innerHeight : 300;

    const closureTimeout = ((setFaceState) => {
        let active = false

        return () => {
            if (!active) {
                active = true
                setTimeout(() => {
                    setFaceState(false)
                    active = false
                    isValid = false
                }, +process.env.REACT_APP_PROOF_OF_LIFE_MAX_SECONDS)
            }
        }
    }
    )(setFaceState)

    // The function passed to useEffect will run after the render is committed to the screen.
    useEffect(() => {
        const loadModels = async () => {
            const MODEL_URL = `${process.env.REACT_APP_REDIRECT_URI}/models`;
            // First we load the necessary models used by face-api.js located in /public/models
            // https://github.com/justadudewhohacks/face-api.js-models
            Promise.all([
                faceApi.nets.tinyFaceDetector.loadFromUri(MODEL_URL)
            ]).then(async () => {
                try {
                    // With HTML5 came the introduction of APIs with access to device hardware, including the MediaDevices API.
                    // This API provides access to multimedia input devices such as audio and video.

                    // Since React does not support the srcObject attribute,
                    // we use a ref to target the video and assign the stream to the srcObject property.
                    videoRefProp.current.srcObject = await navigator.mediaDevices.getUserMedia({
                        audio: false,
                        video: {
                            width: videoWidth,
                            height: videoHeight,
                        },
                    });
                } catch (err) {
                    console.error(err);
                }
            }).catch((err) => console.log(err));
        };
        // We run the function
        loadModels();
    }, []);
    // onPlay={handleVideoOnPlay}
    // React onPlay event execute this function when a video has started to play
    const handleVideoOnPlay = async () => {
        const streamData = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
        setStream(streamData)
        // Let's draw our face detector every 100 milliseconds in <canvas>
        // that is an HTML element which can be used to draw graphics using scripts.
        setInterval(async () => {
            try {
                if (canvasRef.current) {
                    // https://justadudewhohacks.github.io/face-api.js/docs/globals.html#createcanvasfrommedia
                    // We fill our canvas tag with the result obtained from our webcam
                    canvasRef.current.innerHTML = faceApi.createCanvasFromMedia(
                        videoRefProp.current
                    );
                    // We always want to match the canvas to its display size and we can do that with
                    faceApi.matchDimensions(canvasRef.current, {
                        width: videoWidth,
                        height: videoHeight,
                    });
                    // https://justadudewhohacks.github.io/face-api.js/docs/globals.html#detectallfaces
                    // face-api.js detect face in our video
                    const detections = await faceApi
                        .detectAllFaces(
                            videoRefProp.current,
                            new faceApi.TinyFaceDetectorOptions()
                        )

                    // https://justadudewhohacks.github.io/face-api.js/docs/globals.html#resizeResults
                    // const resizedDetections = faceApi.resizeResults(
                    //     detections,
                    //     {
                    //         width: 200,
                    //         height: 200
                    //     }
                    // );

                    if (canvasRef.current) {
                        canvasRef.current
                            .getContext('2d') // The HTMLCanvasElement.getContext() method returns a drawing context on the canvas, or null if the context identifier is not supported.
                            .clearRect(0, 0, videoWidth, videoHeight); // The clearRect() method in HTML canvas is used to clear the pixels in a given rectangle.
                        // Draw our detections, face landmarks and expressions.
                        // faceApi.draw.drawDetections(canvasRef.current, resizedDetections);
                    }
                    if (detections.length > 0 && detections.length < 2) {
                        if (detections[0].score >= 0.70) {
                            const options = {
                                boxColor: "#008000",
                                label: Math.round(detections[0].score * 100)
                            }
                            const drawBox = new faceApi.draw.DrawBox(detections[0].box, options)
                            drawBox.draw(canvasRef.current)
                            if (isValid) return
                            let canvas = document.createElement("canvas")
                            canvas.width = 400;
                            canvas.height = 400;
                            let ctx = canvas.getContext("2d")

                            ctx.drawImage(videoRefProp.current, 0, 0, canvas.width, canvas.height)
                            let dataUrl = canvas.toDataURL("image/jpeg")
                            let dataImage = await fetch(dataUrl).then(res => res.blob())

                            setFaceState(true)
                            isValid = true
                            await startRecording(streamData, dataImage)
                        } else {
                            const options = {
                                boxColor: "#ff0000",
                                label: Math.round(detections[0].score * 100)
                            }
                            const drawBox = new faceApi.draw.DrawBox(detections[0].box, options)
                            drawBox.draw(canvasRef.current)
                        }
                    } else if (detections.length >= 2) {
                        const options = {
                            boxColor: "#ff0000",
                            label: Math.round(detections[0].score * 100)
                        }
                        const drawBox = new faceApi.draw.DrawBox(detections[0].box, options)
                        drawBox.draw(canvasRef.current)
                        isValid = false
                        setFaceState(false)
                    } else {
                        closureTimeout()
                    }
                }
            } catch (err) {
            }
        }, 100);
    };

    return (
        <div style={{ display: 'flex' }}>
            <video
                id='recognition'
                ref={videoRefProp}
                width={videoWidth}
                height={videoHeight}
                playsInline
                autoPlay
                onPlay={handleVideoOnPlay}
            />
            {/* To make our canvas appear on top of our video */}
            <canvas style={{ position: 'absolute' }} ref={canvasRef} />
        </div>
    )
}

export default FaceRecognition